import BaseService from '@root/common/base/BaseService';
import { DocumentNode, OperationVariables } from '@apollo/client/core';

import getNotificationsQuery from '@root/modules/customer/graphql/notification/getNotifications.graphql';
import getNotificationsSimpleQuery from '@root/modules/customer/graphql/notification/getNotificationsSimple.graphql';
import updateNotificationsMutation from '@root/modules/customer/graphql/notification/updateNotification.graphql';

import { GetNotifications, GetNotificationsSimple, NotificationData, UpdateNotification } from '../types/notification';

export interface NotificationState {
  unreadCount: number;
  showProfileDot: boolean;
}

interface MutationRequest {
  mutation: DocumentNode;
}

interface QueryRequest {
  query: DocumentNode;
}

interface RequestOptions<Request> {
  variables?: Request;
  context: {
    headers: {
      Authorization: string;
    };
  };
}

type MutationOptions<Request> = MutationRequest & RequestOptions<Request>;
type QueryOptions<Request> = QueryRequest & RequestOptions<Request>;
type RequestOption = MutationRequest | QueryRequest;

class NotificationService extends BaseService {
  private _customerToken: string | null = null;

  public setCustomerToken(customerToken: string) {
    this._customerToken = customerToken;
  }

  private getQueryOptions<Request>(variables: Request, requestOption: MutationRequest): MutationOptions<Request>;
  private getQueryOptions<Request>(variables: Request, requestOption: QueryRequest): QueryOptions<Request>;
  private getQueryOptions<Request>(variables: Request, requestOption: RequestOption) {
    const options: RequestOptions<Request> = {
      ...requestOption,
      context: {
        headers: {
          Authorization: `Bearer ${this._customerToken}`,
        },
      },
    };
    if (variables) {
      options.variables = variables;
    }
    return options;
  }

  private async query<Response, Request extends OperationVariables | undefined = undefined>({ variables, query }: { variables: Request; query: DocumentNode }) {
    if (!this._customerToken) {
      return;
    }

    const options = this.getQueryOptions(variables, { query });

    const apiProvider = this.createProvider('GraphQL');
    apiProvider.selectAPI('notification-api');

    const [response] = await apiProvider.query<Response>(options);
    return response.data;
  }

  private async mutation<Response, Request extends OperationVariables | undefined = undefined>({
    variables,
    mutation,
  }: {
    variables?: Request;
    mutation: DocumentNode;
  }) {
    if (!this._customerToken) {
      return;
    }

    const options = this.getQueryOptions(variables, { mutation });

    const apiProvider = this.createProvider('GraphQL');
    apiProvider.selectAPI('notification-api');

    const response = await apiProvider.mutation<Response>(options);
    return response.data || undefined;
  }

  async getNotifications(): Promise<NotificationData[]> {
    const variables = {
      input: {
        type: 'WEB',
        createdAt: {
          since: 'SINCE_365_DAYS',
        },
        status: ['NOT_SEEN', 'SEEN'],
        pager: {
          limit: 100,
          offset: 0,
        },
      },
    };

    const response = await this.query<GetNotifications, GetNotifications['variables']>({ variables, query: getNotificationsQuery });

    return response?.data.items || [];
  }

  async loadUnreadCount(): Promise<number> {
    const variables = {
      input: {
        type: 'WEB',
        createdAt: {
          since: 'SINCE_365_DAYS',
        },
        status: ['NOT_SEEN'],
        pager: {
          limit: 100,
          offset: 0,
        },
      },
    };

    const response = await this.query<GetNotificationsSimple, GetNotificationsSimple['variables']>({ variables, query: getNotificationsSimpleQuery });

    return response?.data.items.length ?? 0;
  }

  async updateNotification(id: string) {
    const variables = {
      input: {
        id,
        status: 'SEEN',
        type: 'WEB',
      },
    };

    await this.mutation<UpdateNotification, UpdateNotification['variables']>({ variables, mutation: updateNotificationsMutation });
  }
}

export default NotificationService;
